import {memo, useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Image as BaseImage, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import Link from '@salesforce/retail-react-app/app/components/link';

/**
 * @override
 * Icon component
 * - Renders icon with image or SVG code
 * - Displays name and description
 * - Wraps content in Link if provided
 * - Handles cases where icon is missing but other fields exist
 *
 * @param {Object} props - Component props
 * @param {Contentful.Icon} props.component - Icon component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
const IconComponent = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/icon', {component, isComplete, isFetching});

  const {name, svgCode, iconImage, description, link} = component || {};

  const iconImageUrl = iconImage?.url;
  const iconImageTitle = iconImage?.title;

  const linkUrl = link?.url || null;
  const linkOpenInNewTab = link?.openInNewTab;
  const linkTarget = linkOpenInNewTab ? '_blank' : '_self';

  const hasContent = useMemo(() => {
    return Boolean(name || iconImageUrl || svgCode || description);
  }, [name, iconImageUrl, svgCode, description]);

  const ariaLabel = useMemo(() => {
    if (name) return name;
    if (description) return description;
    if (iconImageTitle) return iconImageTitle;

    return 'Icon';
  }, [name, description, iconImageTitle]);

  if (!isComplete && isFetching) {
    return (
      <Box {...styles.root} {...props}>
        <Skeleton height="64px" mb={4} width="64px" />
        <Skeleton height="16px" mb={2} width="80px" />
        <Skeleton height="14px" width="120px" />
      </Box>
    );
  }

  if (!hasContent) {
    return null;
  }

  const iconContent = (() => {
    if (iconImageUrl) {
      return (
        <BaseImage
          alt={iconImageTitle || name || ''}
          src={iconImageUrl}
          {...styles.icon}
        />
      );
    }

    if (svgCode) {
      return (
        <Box
          {...styles.icon}
          dangerouslySetInnerHTML={{__html: svgCode}}
        />
      );
    }

    return null;
  })();

  const content = (
    <Box {...styles.root} {...props} aria-label={ariaLabel} role="img">
      {iconContent}
      {name && (
        <Text {...styles.title}>
          {name}
        </Text>
      )}
      {description && (
        <Text {...styles.description}>
          {description}
        </Text>
      )}
    </Box>
  );

  if (linkUrl) {
    const linkAriaLabel = link?.text || ariaLabel;

    return (
      <Box {...styles.linkWrapper}>
        {/* Currently, we use Link component from @salesforce/retail-react-app/app/components/link
          due to Contentful Link component has not been implemented yet*/}
        <Link aria-label={linkAriaLabel} target={linkTarget} to={linkUrl} {...styles.link}>
          {content}
        </Link>
      </Box>
    );
  }

  return content;
};

IconComponent.displayName = 'Icon';
IconComponent.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};

export const Icon = memo(IconComponent, (prevProps, nextProps) => {
  return (
    prevProps.component === nextProps.component &&
    prevProps.isComplete === nextProps.isComplete &&
    prevProps.isFetching === nextProps.isFetching
  );
});
